import { useState } from "react";
import { Check } from "lucide-react";
import PageLayout from "../components/site/PageLayout";
import { Reveal } from "../components/site/Reveal";
import { SectionHeading } from "../components/site/SectionHeading";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useContent } from "../lib/content";
import { submitForm } from "../lib/forms";
import { toast } from "sonner";

const Volunteer = () => {
  const { content } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", school: "", grade: "", availability: "", interests: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitForm("volunteer", form);
      toast.success("Application received! We'll be in touch soon.");
      setDone(true);
    } catch {
      toast.error("Could not submit — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <PageLayout title="Volunteer">
      <section className="relative pt-16 pb-10 overflow-hidden">
        <div className="ambient" aria-hidden />
        <div className="ambient-grid" aria-hidden />
        <div className="container-tt">
          <SectionHeading eyebrow="Volunteer" title="Join TechTies." description={content.volunteer.intro} />
        </div>
      </section>

      <section className="py-14">
        <div className="container-tt grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div>
                <h3 className="font-display text-2xl text-stone">What you'll get out of it</h3>
                <p className="mt-4 text-stone/85 leading-relaxed">{content.volunteer.benefitsBody}</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div>
                <h3 className="font-display text-2xl text-stone">What we look for</h3>
                <ul className="mt-4 space-y-3">
                  {content.volunteer.requirements.map(b => (
                    <li key={b} className="flex gap-3"><span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-skyDark"><Check size={13} /></span><span>{b}</span></li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="card-surface p-8 md:p-10">
                {done ? (
                  <div data-testid="volunteer-success" className="py-10 text-center">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full btn-sky-fill text-white mx-auto shadow-sky"><Check size={28} /></div>
                    <h3 className="font-display text-3xl mt-6">Thank you!</h3>
                    <p className="mt-3 text-muted-foreground">Your application has been received. We'll follow up by email within a few days.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="grid gap-5" data-testid="volunteer-form">
                    <h3 className="font-display text-2xl text-stone">Volunteer application</h3>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div><Label>Full name *</Label><Input required data-testid="v-name" className="h-12 mt-2 bg-white rounded-xl" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                      <div><Label>Email *</Label><Input required type="email" data-testid="v-email" className="h-12 mt-2 bg-white rounded-xl" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                      <div><Label>Phone</Label><Input data-testid="v-phone" className="h-12 mt-2 bg-white rounded-xl" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                      <div><Label>School</Label><Input data-testid="v-school" className="h-12 mt-2 bg-white rounded-xl" value={form.school} onChange={e => setForm({...form, school: e.target.value})} /></div>
                      <div><Label>Grade</Label><Input data-testid="v-grade" className="h-12 mt-2 bg-white rounded-xl" value={form.grade} onChange={e => setForm({...form, grade: e.target.value})} /></div>
                      <div><Label>Availability</Label><Input placeholder="Weekends, after school…" data-testid="v-avail" className="h-12 mt-2 bg-white rounded-xl" value={form.availability} onChange={e => setForm({...form, availability: e.target.value})} /></div>
                    </div>
                    <div><Label>Areas of interest</Label><Input placeholder="Tech help, AI, community games…" data-testid="v-interests" className="h-12 mt-2 bg-white rounded-xl" value={form.interests} onChange={e => setForm({...form, interests: e.target.value})} /></div>
                    <div><Label>Anything else?</Label><Textarea data-testid="v-message" rows={4} className="mt-2 bg-white rounded-xl" value={form.message} onChange={e => setForm({...form, message: e.target.value})} /></div>
                    <Button data-testid="v-submit" disabled={busy} type="submit" className="btn-pill btn-motion btn-sky-fill h-12 text-white shadow-sky hover:shadow-skyHover border-0">
                      {busy ? "Submitting…" : "Submit application"}
                    </Button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Volunteer;
